/**
 * Settings: brand name, contact e-mail, a fresh sign-in link.
 *
 * The live page also has a "Description" textarea. It is NOT ported, because it
 * does nothing: there is no `description` column on `brands`, and
 * POST /api/brand/update builds its update from an explicit allowlist of `name`
 * and `email`. Typing a description, pressing Save and being told "Saved." threw
 * the text away and said it had worked - which is worse than the field not
 * existing. Verified end to end: the request returns 200 and nothing about the
 * description survives anywhere.
 *
 * If Aditor wants brand descriptions it needs a column, the allowlist widened,
 * and the field returned by GET /api/brand. That is a server change and a
 * product decision, not something to fake on this screen.
 *
 * @module web/screens/app/SettingsScreen
 */
import { useEffect, useState } from "react";
import { post } from "../../api/client.ts";
import { useBrand } from "../../state/BrandContext.tsx";

export function SettingsScreen() {
  const { brand, refresh, notify } = useBrand();
  const [name, setName] = useState(brand?.name ?? "");
  const [email, setEmail] = useState(brand?.email ?? "");
  const [contactName, setContactName] = useState(brand?.contactName ?? "");
  const [busy, setBusy] = useState(false);
  const [confirmingLink, setConfirmingLink] = useState(false);
  const owner = (brand?.role ?? "owner") === "owner";

  // The brand can arrive after first render (bootstrap) or change under us (a
  // refresh after a webhook), so the fields follow it until they are edited.
  useEffect(() => {
    if (brand) { setName(brand.name); setEmail(brand.email); setContactName(brand.contactName ?? ""); }
  }, [brand?.id]);

  async function save(): Promise<void> {
    setBusy(true);
    try {
      await post("/api/brand/update", { name: name.trim(), email: email.trim(), contactName: contactName.trim() });
      await refresh();
      notify("Saved.", "success");
    } catch (e) {
      notify(e instanceof Error ? e.message : "Failed to save.", "error");
    }
    setBusy(false);
  }

  async function sendLink(): Promise<void> {
    const to = brand?.self?.email;
    if (!to) { notify("Set a primary contact email first.", "error"); return; }
    setBusy(true);
    try {
      // Unauthenticated by design and always answers 200, so there is nothing to
      // read back: it must not reveal whether an address is known.
      await fetch("/api/auth/request-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: to }),
      });
      notify(`Sent - check ${to}.`, "success");
    } catch {
      notify("Could not send the link. Check your connection.", "error");
    }
    setBusy(false);
    setConfirmingLink(false);
  }

  return (
    <>
      <header className="page-head fade-in">
        <h1 className="t-h2">Settings</h1>
        <p className="t-body" style={{ marginTop: 4 }}>Brand details, notifications, and account.</p>
      </header>

      <div className="rule" style={{ margin: "32px 0 14px" }}>Brand</div>
      {owner ? <div className="card fade-in" style={{ maxWidth: 640 }}>
        <label className="input-label" htmlFor="set-contact">Your name</label>
        <div className="input-well">
          <input id="set-contact" type="text" autoComplete="name" maxLength={120} value={contactName} onChange={e => setContactName(e.target.value)} />
        </div>
        <div style={{ height: 16 }} />
        <label className="input-label" htmlFor="set-name">Brand name</label>
        <div className="input-well">
          <input id="set-name" type="text" placeholder="Your brand name" value={name} onChange={e => setName(e.target.value)} />
        </div>

        <label className="input-label" style={{ marginTop: 16 }} htmlFor="set-email">Primary contact email</label>
        <div className="input-well">
          <input id="set-email" type="email" placeholder="hello@yourbrand.com" value={email} onChange={e => setEmail(e.target.value)} />
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button className="btn btn-brand" disabled={busy} onClick={() => void save()}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div> : <div className="card fade-in" style={{ maxWidth: 640 }}>
        <p className="t-body-sm">{brand?.name} · Brand details are managed by an owner.</p>
      </div>}

      <div className="rule" style={{ margin: "32px 0 14px" }}>Notifications</div>
      <div className="card" style={{ maxWidth: 640 }}>
        <p className="t-body">Notification preferences are managed in your email client for now.</p>
      </div>

      <div className="rule" style={{ margin: "32px 0 14px" }}>Account</div>
      <div className="card" style={{ maxWidth: 640 }}>
        {brand?.self?.email && <button className="btn btn-ghost" onClick={() => setConfirmingLink(true)}>
          Email me a new magic link
        </button>}
      </div>

      {confirmingLink && (
        <>
          <div className="pay-sheet-overlay open" onClick={() => setConfirmingLink(false)} />
          <div className="pay-sheet open" data-state="ask" role="dialog" aria-modal="true">
            <div className="pay-grip" />
            <div className="pay-body">
              <div className="pay-eyebrow">New sign-in link</div>
              <h2 className="pay-title">{brand?.self?.email ?? "your email"}</h2>
              <button className="pay-cta" disabled={busy} onClick={() => void sendLink()}>
                {busy ? "Sending…" : "Send the link"}
              </button>
              <button className="pay-cancel" onClick={() => setConfirmingLink(false)}>Cancel</button>
              <p className="pay-note">
                Your existing links keep working - this adds one, it does not
                replace them.
              </p>
            </div>
          </div>
        </>
      )}
    </>
  );
}
